// src/components/FileManagerPage.jsx
import React, { useEffect, useState, useCallback, useMemo } from "react";
import styled from "styled-components";
import {
  listOrgArticles,
  getArticle,
  deleteArticle,
  updateArticle,
  renameArticle,
  getDisplayStatus,
} from "../lib/articles";
import { listProjectsForUser } from "../lib/projects";
import { useNavigate, useParams } from "react-router-dom";
import GenerateModal from "./ContentGeneration/GenerateModal";
import { generateContent } from "./ContentGeneration/generateContent";

// ---- layout / styling ----
const PageWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;

const PageCard = styled.div`
  width: 100%;
  max-width: 1100px;
  background: #ffffff;
  padding: 24px 10px 28px;
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #1f2933;
`;

const ProjectBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const Label = styled.span`
  font-size: 14px;
  color: #4b5563;
`;

const Select = styled.select`
  font-size: 14px;
  padding: 6px 10px;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  min-width: 220px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  table-layout: fixed;

  th,
  td {
    padding: 8px 10px;
    text-align: left;
  }

  th {
    font-weight: 600;
    color: #6b7280;
  }

  tbody tr:hover {
    background-color: #f3f4f6;
  }

  th:nth-child(1),
  td:nth-child(1) {
    width: 30%;
    max-width: 30%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  th:nth-child(4),
  td:nth-child(4) {
    width: max-content;
    min-width: max-content;
    white-space: nowrap;
  }

  th:nth-child(5),
  td:nth-child(5) {
    width: 70px;
    max-width: 70px;
    min-width: 70px;
    white-space: nowrap;
    text-align: center;
  }

  th:nth-child(7),
  td:nth-child(7) {
    width: 90px;
    min-width: 90px;
    max-width: 90px;
  }
`;

const ActionsCell = styled.td`
  position: relative;
`;

const Input = styled.input`
  font-size: 14px;
  padding: 4px 6px;
  border-radius: 4px;
  border: 1px solid #d1d5db;
  width: 100%;
`;

const MenuButton = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 4px 8px;
  font-size: 20px;
  line-height: 1;
  color: #6b7280; /* gray-500 */
  border-radius: 999px;

  &:hover {
    background: #f3f4f6; /* gray-100 */
    color: #111827; /* gray-900 */
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  right: 0;
  top: 100%;
  margin-top: 4px;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.18);
  padding: 4px 0;
  min-width: 140px;
  z-index: 10;
`;

const DropdownItem = styled.button`
  width: 100%;
  text-align: left;
  border: none;
  background: transparent;
  padding: 8px 12px;
  font-size: 14px;
  color: #111827;
  cursor: pointer;

  &:hover {
    background: #f3f4f6;
  }

  &.danger {
    color: #b91c1c; /* red-700 */
  }
`;

const NewArticleButton = styled.button`
  margin-left: auto; /* pushes it to the far right */
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid #d0d7de;
  background: #00876e;
  color: white;
  cursor: pointer;
`;

const ProgressPill = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 1px 10px;
  border-radius: 300px;
  text-align: center;
  font-size: 12px;
  font-weight: 700;
  color: ${({ status }) => {
    switch (status) {
      case "Edits Needed":
        return "#e8394f";
      case "Ready for Review":
        return "#00876e";
      case "Ready for Approval":
        return "#00876e";
      case "Ready to Publish":
        return "#1764e8";
      case "In Progress":
      default:
        return "#a46126";
    }
  }};
  background: ${({ status }) => {
    switch (status) {
      case "Edits Needed":
        return "#fbe8ec";
      case "Ready for Review":
        return "#e0f0ee";
      case "Ready for Approval":
        return "#e0f0ee";
      case "Ready to Publish":
        return "#ccdeff";
      case "In Progress":
      default:
        return "#fef7e6";
    }
  }};
`;

function GeneratingOverlay({ message }) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(0,0,0,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        style={{
          background: "#111",
          color: "#fff",
          borderRadius: 12,
          padding: "22px 26px",
          width: "min(520px, 92vw)",
          boxShadow: "0 10px 30px rgba(0,0,0,0.35)",
          whiteSpace: "pre-line",
          textAlign: "center",
          fontSize: 18,
          lineHeight: 1.35,
        }}
      >
        {message}
      </div>
    </div>
  );
}

// ---- FULL PAGE FILE MANAGER ----
export default function FileManagerPage({
  onOpenArticle,
  beginGenerating,
  endGenerating,
  profile,
  setFeedback,
  orgId,
  currentUserId,
  currentUserRole,
  organization,
}) {
  const isAdmin =
    profile?.role === "Admin" ||
    profile?.role === "superAdmin" ||
    profile?.isTrueSuperAdmin === true;

  const [rows, setRows] = useState([]);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  // New state for reassigning
  const [reassigningId, setReassigningId] = useState(null);

  const [openMenuId, setOpenMenuId] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState("__ALL__");
  const [sortConfig, setSortConfig] = useState({
    key: "updated_at",
    direction: "desc",
  });

  const navigate = useNavigate();

  const [isGenerateOpen, setIsGenerateOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatingMessage, setGeneratingMessage] = useState("");

  const getProjectLabel = useCallback(
    (row) => {
      if (!row.project_id) return "Unassigned";
      return projects.find((p) => p.id === row.project_id)?.title || "Unknown";
    },
    [projects]
  );

  const handleOpenArticle = async (article) => {
    try {
      const full = article?.content_html
        ? article
        : await getArticle(article.id);

      if (typeof onOpenArticle === "function") {
        onOpenArticle(full);
      }
    } catch (err) {
      console.error("Error opening article:", err);
      alert("Failed to open article.");
    } finally {
      setOpenMenuId(null);
    }
  };

  const handleGenerateSubmit = async (briefPayload) => {
    try {
      setIsGenerating(true);
      setIsGenerateOpen(false);
      setGeneratingMessage(
        "Generating your article...\nThis may take a few minutes."
      );

      const result = await generateContent(briefPayload, setFeedback);

      const generatedArticleForTextBox = {
        id: result?.id ?? null,
        title: result?.title ?? "Untitled Article",
        project_id: result?.project_id ?? null,
        content_html: result?.Article ?? result?.html ?? result?.content ?? "",
        meta_tags: result?.meta_tags ?? result?.["Meta Tags"] ?? "",
        content_brief: briefPayload, // ✅ Pass the brief so TextBox can read & save it
      };

      navigate(`/organization-portal/${orgId}/content-generation`, {
        state: { openArticle: generatedArticleForTextBox },
      });
    } catch (err) {
      console.error("Generate failed:", err);
      alert("Failed to generate article.");
    } finally {
      setIsGenerating(false);
      setGeneratingMessage("");
    }
  };

  // Load projects
  useEffect(() => {
    if (!orgId || !currentUserId) return;

    (async () => {
      try {
        beginGenerating?.("Loading your projects…");

        const list = await listProjectsForUser({
          user_id: currentUserId,
          role: currentUserRole,
          company_id: orgId,
        });

        const augmented = [
          { id: "__ALL__", title: "All Projects" },
          { id: "__UNASSIGNED__", title: "Unassigned Files" },
          ...list,
        ];
        setProjects(augmented);
        setSelectedProjectId("__ALL__");
      } catch (e) {
        console.error("Error loading projects in FileManagerPage:", e);
      } finally {
        endGenerating?.();
      }
    })();
  }, [orgId, currentUserId, currentUserRole]);

  // Load articles
  useEffect(() => {
    if (!orgId) return;

    (async () => {
      try {
        beginGenerating?.("Loading files…");

        let project_id;

        if (selectedProjectId === "__ALL__") {
          project_id = undefined;
        } else if (selectedProjectId === "__UNASSIGNED__") {
          project_id = null;
        } else {
          project_id = selectedProjectId;
        }

        const list = await listOrgArticles({ project_id, org_id: orgId });
        setRows(list);
      } catch (e) {
        console.error("Error loading articles in FileManagerPage:", e);
      } finally {
        endGenerating?.();
      }
    })();
  }, [selectedProjectId, orgId]);

  const sortedRows = useMemo(() => {
    const data = [...rows];

    data.sort((a, b) => {
      let aVal;
      let bVal;

      switch (sortConfig.key) {
        case "title":
          aVal = a.title || "";
          bVal = b.title || "";
          break;

        case "project":
          aVal = getProjectLabel(a);
          bVal = getProjectLabel(b);
          break;

        case "creator":
          aVal = a.creator_name || a.created_by || "";
          bVal = b.creator_name || b.created_by || "";
          break;

        case "status":
          aVal = a.progress || "In Progress";
          bVal = b.progress || "In Progress";
          break;

        case "score":
          const sa = a.score || 0;
          const sb = b.score || 0;
          return sortConfig.direction === "asc" ? sa - sb : sb - sa;

        case "updated_at": {
          const aTime = a.updated_at ? new Date(a.updated_at).getTime() : 0;
          const bTime = b.updated_at ? new Date(b.updated_at).getTime() : 0;
          return sortConfig.direction === "asc" ? aTime - bTime : bTime - aTime;
        }

        default:
          return 0;
      }

      if (typeof aVal === "string" && typeof bVal === "string") {
        const cmp = aVal.localeCompare(bVal, undefined, {
          sensitivity: "base",
        });
        return sortConfig.direction === "asc" ? cmp : -cmp;
      }
      return 0;
    });

    return data;
  }, [rows, sortConfig, getProjectLabel]);

  const handleRenameCommit = async (rowId) => {
    if (!isAdmin) return;

    try {
      await renameArticle(rowId, renameValue);
      setRows((prev) =>
        prev.map((x) => (x.id === rowId ? { ...x, title: renameValue } : x))
      );
    } catch (e) {
      console.error("Error renaming article:", e);
      alert("Failed to rename article.");
    } finally {
      setRenamingId(null);
      setRenameValue("");
    }
  };

  // ✅ Handle changing the project
  const handleReassign = async (rowId, newProjectId) => {
    // Double check safe guard
    if ((currentUserRole || "").toLowerCase() !== "admin") {
      alert("Only admins can reassign projects.");
      setReassigningId(null);
      return;
    }

    try {
      const pid = newProjectId === "" ? null : newProjectId;
      await updateArticle(rowId, { project_id: pid });

      // Update local state immediately
      setRows((prev) =>
        prev.map((x) => (x.id === rowId ? { ...x, project_id: pid } : x))
      );
    } catch (e) {
      console.error("Error reassigning article:", e);
      alert("Failed to reassign article.");
    } finally {
      setReassigningId(null);
    }
  };

  const handleDelete = async (rowId) => {
    if (!isAdmin) return;
    if (!window.confirm("Delete this article? This cannot be undone.")) return;

    try {
      await deleteArticle(rowId);
      setRows((prev) => prev.filter((x) => x.id !== rowId));
    } catch (e) {
      console.error("Error deleting article:", e);
      alert("Failed to delete article.");
    }
  };

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key, direction: "asc" };
    });
  };

  const renderSortIndicator = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === "asc" ? " ▲" : " ▼";
  };

  return (
    <PageWrapper>
      <PageCard>
        <Head>
          <Title>My Articles</Title>
        </Head>

        <ProjectBar>
          <Label>Project</Label>
          <Select
            value={selectedProjectId}
            onChange={(e) => setSelectedProjectId(e.target.value)}
          >
            {projects.length === 0 ? (
              <option value="">No projects</option>
            ) : (
              projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.title}
                </option>
              ))
            )}
          </Select>
          {organization?.ai_enabled ? (
            <NewArticleButton
              type="button"
              onClick={() => setIsGenerateOpen(true)}
            >
              New Article
            </NewArticleButton>
          ) : (
            <NewArticleButton
              type="button"
              onClick={() =>
                alert(
                  "Article request submitted! (Wire this up to your backend)"
                )
              }
            >
              Submit Request
            </NewArticleButton>
          )}
        </ProjectBar>

        <Table>
          <thead>
            <tr>
              <th
                onClick={() => handleSort("title")}
                style={{ cursor: "pointer" }}
              >
                Title{renderSortIndicator("title")}
              </th>
              <th
                onClick={() => handleSort("project")}
                style={{ cursor: "pointer" }}
              >
                Project{renderSortIndicator("project")}
              </th>
              <th
                onClick={() => handleSort("creator")}
                style={{ cursor: "pointer" }}
              >
                Writer{renderSortIndicator("creator")}
              </th>
              <th
                onClick={() => handleSort("status")}
                style={{ cursor: "pointer" }}
              >
                Status{renderSortIndicator("status")}
              </th>
              <th
                onClick={() => handleSort("score")}
                style={{ cursor: "pointer" }}
              >
                Score{renderSortIndicator("score")}
              </th>
              <th
                onClick={() => handleSort("updated_at")}
                style={{ cursor: "pointer" }}
              >
                Last Updated{renderSortIndicator("updated_at")}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((r) => (
              <tr key={r.id}>
                <td>
                  {renamingId === r.id ? (
                    <Input
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleRenameCommit(r.id);
                        } else if (e.key === "Escape") {
                          setRenamingId(null);
                          setRenameValue("");
                        }
                      }}
                      autoFocus
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleOpenArticle(r)}
                      style={{
                        background: "transparent",
                        border: "none",
                        padding: 0,
                        margin: 0,
                        cursor: "pointer",
                        textAlign: "left",
                        font: "inherit",
                        color: "inherit",
                        fontWeight: 800,
                        width: "100%",
                      }}
                      title="Open article"
                    >
                      {r.title || "Untitled"}
                    </button>
                  )}
                </td>

                <td>
                  {/* ✅ Check if we are reassigning this row */}
                  {reassigningId === r.id ? (
                    <select
                      autoFocus
                      value={r.project_id || ""}
                      onChange={(e) => handleReassign(r.id, e.target.value)}
                      onBlur={() => setReassigningId(null)}
                      style={{
                        width: "100%",
                        padding: "4px 6px",
                        borderRadius: "4px",
                        border: "1px solid #d1d5db",
                        fontSize: "14px",
                      }}
                    >
                      <option value="">Unassigned</option>
                      {projects
                        .filter(
                          (p) => p.id !== "__ALL__" && p.id !== "__UNASSIGNED__"
                        )
                        .map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.title}
                          </option>
                        ))}
                    </select>
                  ) : (
                    getProjectLabel(r)
                  )}
                </td>

                <td>{r.creator_name || r.created_by || "—"}</td>
                <td>
                  <ProgressPill
                    status={getDisplayStatus(r.progress, organization?.ai_enabled)}
                  >
                    {getDisplayStatus(r.progress, organization?.ai_enabled)}
                  </ProgressPill>
                </td>

                <td style={{ textAlign: "center" }}>{r.score ?? "-"}</td>

                <td>
                  {r.updated_at ? new Date(r.updated_at).toLocaleString() : "—"}
                </td>
                <ActionsCell>
                  <MenuButton
                    type="button"
                    onClick={() =>
                      setOpenMenuId((current) =>
                        current === r.id ? null : r.id
                      )
                    }
                  >
                    ⋯
                  </MenuButton>

                  {openMenuId === r.id && (
                    <DropdownMenu>
                      <DropdownItem
                        type="button"
                        onClick={async () => {
                          try {
                            beginGenerating?.("Opening article…");
                            const article = await getArticle(r.id);
                            onOpenArticle?.(article);
                          } catch (err) {
                            console.error("Error opening article:", err);
                          } finally {
                            setOpenMenuId(null);
                            endGenerating?.();
                          }
                        }}
                      >
                        Open
                      </DropdownItem>

                      {/* ✅ Only show if Admin */}
                      {(currentUserRole || "").toLowerCase() === "admin" && (
                        <DropdownItem
                          type="button"
                          onClick={() => {
                            setReassigningId(r.id);
                            setOpenMenuId(null);
                            setRenamingId(null);
                          }}
                        >
                          Reassign
                        </DropdownItem>
                      )}

                      {isAdmin && (
                        <DropdownItem
                          type="button"
                          onClick={() => {
                            setRenamingId(r.id);
                            setRenameValue(r.title || "");
                            setOpenMenuId(null);
                            setReassigningId(null);
                          }}
                        >
                          Rename
                        </DropdownItem>
                      )}

                      {isAdmin && (
                        <DropdownItem
                          type="button"
                          className="danger"
                          onClick={() => {
                            setOpenMenuId(null);
                            handleDelete(r.id);
                          }}
                        >
                          Delete
                        </DropdownItem>
                      )}
                    </DropdownMenu>
                  )}
                </ActionsCell>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={7}>No articles found.</td>
              </tr>
            )}
          </tbody>
        </Table>
      </PageCard>
      <GenerateModal
        isOpen={isGenerateOpen}
        onClose={() => setIsGenerateOpen(false)}
        onSubmit={handleGenerateSubmit}
        currentUser={{ id: profile?.id, role: profile?.role }}
        projects={projects || []}
      />
      {isGenerating && <GeneratingOverlay message={generatingMessage} />}
    </PageWrapper>
  );
}
